import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Linkedin, Eye, MousePointerClick, UserPlus, MessageCircle, TrendingUp, Medal } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={18} style={{ color }} />
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-3xl font-bold text-gray-900">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

export default function AdminCampaign() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    fetch(`${API_URL}/api/campaign/admin/linkedin-funnel`, { headers })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setData)
      .catch(e => setError(String(e)));
  }, [headers]);

  const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');
  const f = data?.founding_families;

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-campaign-page">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-4" data-testid="campaign-back-btn">
          <ArrowLeft size={16} /> Command Centre
        </button>
        <div className="flex items-center gap-3 mb-1">
          <Linkedin size={26} className="text-[#0A66C2]" />
          <h1 className="text-2xl font-bold text-gray-900">LinkedIn Launch Campaign</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">Sept 16 founder-led posts → Free Practice Sessions funnel · attribution via utm_source=linkedin</p>

        {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">Failed to load: {error}</div>}
        {!data && !error && <div className="text-sm text-gray-500">Loading…</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="campaign-funnel-cards">
              <StatCard icon={Eye} label="Landing Views" value={data.landing_views} color="#7B4FD8" />
              <StatCard icon={MousePointerClick} label="CTA Clicks" value={data.cta_clicks} sub={`${pct(data.cta_clicks, data.landing_views)} of views`} color="#F5831F" />
              <StatCard icon={UserPlus} label="Signups" value={data.signups} sub="utm_source=linkedin" color="#2EAA5C" />
              <StatCard icon={MessageCircle} label="Activated" value={data.activated} sub={`${pct(data.activated, data.signups)} had ≥1 conversation`} color="#0A66C2" />
              <StatCard icon={TrendingUp} label="Upgraded" value={data.upgraded} sub={`${pct(data.upgraded, data.signups)} of signups`} color="#DC2626" />
            </div>

            {/* Founding Families */}
            <div className="bg-white rounded-lg p-5 mb-8" style={{ border: '1px solid #E0E0E0' }} data-testid="founding-families-card">
              <div className="flex items-center gap-2 mb-3">
                <Medal size={18} className="text-amber-500" />
                <h2 className="font-bold text-gray-900">Founding Families Offer — Brave annual, extra {f?.percent_off}% off</h2>
                <span className={`ml-auto px-2 py-0.5 text-xs font-bold rounded-full ${f?.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {f?.active ? 'ACTIVE' : 'ENDED'}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div><div className="text-gray-500 text-xs">Offer price</div><div className="font-bold">£{(f?.price_pence / 100).toFixed(2)} <span className="text-gray-400 line-through font-normal">£{(f?.full_price_pence / 100).toFixed(0)}</span></div></div>
                <div><div className="text-gray-500 text-xs">Places remaining</div><div className="font-bold">{f?.remaining} / {f?.cap}</div></div>
                <div><div className="text-gray-500 text-xs">Redeemed</div><div className="font-bold">{(f?.cap ?? 0) - (f?.remaining ?? 0)}</div></div>
                <div><div className="text-gray-500 text-xs">Ends</div><div className="font-bold">{f?.ends_at?.slice(0, 10)}</div></div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="views-by-page-table">
                <h2 className="font-bold text-gray-900 mb-3">Landing views by page / post</h2>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-gray-500 border-b"><th className="pb-2">Page</th><th className="pb-2">Campaign</th><th className="pb-2 text-right">Views</th></tr></thead>
                  <tbody>
                    {data.views_by_page.length === 0 && <tr><td colSpan="3" className="py-3 text-gray-400 text-center">No landing views tracked yet</td></tr>}
                    {data.views_by_page.map((v, i) => (
                      <tr key={i} className="border-b border-gray-50"><td className="py-2 font-mono text-xs">{v.page}</td><td className="py-2 text-xs">{v.campaign || '—'}</td><td className="py-2 text-right font-semibold">{v.views}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="signups-by-campaign-table">
                <h2 className="font-bold text-gray-900 mb-3">Signups by LinkedIn post (utm_campaign)</h2>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-gray-500 border-b"><th className="pb-2">Campaign</th><th className="pb-2 text-right">Signups</th></tr></thead>
                  <tbody>
                    {data.signups_by_campaign.length === 0 && <tr><td colSpan="2" className="py-3 text-gray-400 text-center">No LinkedIn-attributed signups yet</td></tr>}
                    {data.signups_by_campaign.map((c, i) => (
                      <tr key={i} className="border-b border-gray-50"><td className="py-2">{c.campaign}</td><td className="py-2 text-right font-semibold">{c.signups}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
